import { getLocalTimeZone, today } from "@internationalized/date";
import { AddOutline24Icon } from "@vygruppen/spor-icon-react";
import { ErrorBoundary } from "app/ErrorBoundary/ErrorBoundaryDashboard";
import {
  ChecklistHistoryWrapper,
  NoDivOverflowButton,
} from "features/CenterContent/RoleContent/VehicleChecklist/ChecklistCustomStyle";
import ChecklistModal from "features/CenterContent/RoleContent/VehicleChecklist/ChecklistModal";
import ChecklistTable from "features/CenterContent/RoleContent/VehicleChecklist/ChecklistTable";
import { FilterChips } from "features/CenterContent/RoleContent/VehicleChecklist/components/FilterChips";
import { SearchCombobox } from "features/CenterContent/RoleContent/VehicleChecklist/components/SearchCombobox";
import { useChecklistFilters } from "features/CenterContent/RoleContent/VehicleChecklist/hooks/useChecklistFilters";
import { useChecklistSearch } from "features/CenterContent/RoleContent/VehicleChecklist/hooks/useChecklistSearch";
import {
  Employee,
  useEmployeeSearch,
} from "features/CenterContent/RoleContent/VehicleChecklist/hooks/useEmployeeSearch";
import useFillouts from "features/CenterContent/RoleContent/VehicleChecklist/hooks/useFillouts";
import { useFetchVehicleSetVariants } from "features/CenterContent/RoleContent/VehicleChecklist/hooks/useVehicleSetVariants";
import { hasDashOrDigit } from "features/CenterContent/RoleContent/VehicleChecklist/utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Flex, Text, VStack } from "shared/components/Spor";
import { useDropsRole } from "stores/useDropsRole";

export const checklistQueryKey = (searchQuery: string = "") => [
  "checklist",
  searchQuery,
];

const Checklist = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);

  const { isLokleder } = useDropsRole();

  const fetchVehicleSetVariants = useFetchVehicleSetVariants(
    today(getLocalTimeZone()).toString(),
  );

  const vehicleSetVariants =
    fetchVehicleSetVariants.data?.vehicleSetVariants || [];

  const {
    searchQuery,
    autoCompletedQuery,
    searchable,
    handleSearchQueryChange,
  } = useChecklistSearch(vehicleSetVariants, employees);

  const { data: employeesData, isLoading } = useEmployeeSearch(
    hasDashOrDigit(searchQuery) ? searchQuery : "",
  );

  useEffect(() => {
    if (employeesData && employeesData.length > 0) {
      setEmployees(employeesData);
    }
  }, [employeesData]);

  const {
    data: checklistFillOuts,
    refetch,
    isLoading: isChecklistLoading,
  } = useFillouts(autoCompletedQuery);

  const fillOuts = useMemo(
    () => checklistFillOuts?.fillOuts ?? [],
    [checklistFillOuts],
  );

  const {
    filter,
    showAll,
    filteredFillOuts,
    displayedFillOuts,
    handleFilterToggle,
    toggleShowAll,
  } = useChecklistFilters(fillOuts);

  const handleModalOpen = useCallback(() => setModalOpen(true), []);
  const handleModalClose = useCallback(() => setModalOpen(false), []);

  return (
    <ChecklistHistoryWrapper>
      <ErrorBoundary>
        <VStack>
          <Box maxWidth="1600px" width="100%">
            <Flex
              w="100%"
              justifyContent="space-between"
              alignItems="center"
              mt="24px"
              mb="12px"
              flexWrap="wrap"
              gap="12px"
              containerType="inline-size" // This makes the search bar responsive to width
            >
              <SearchCombobox
                searchQuery={searchQuery}
                onInputChange={handleSearchQueryChange}
                searchable={searchable}
                isLoading={isLoading}
              />
              <Flex justifyContent="space-between" flex="1">
                <FilterChips
                  filter={filter}
                  onFilterToggle={handleFilterToggle}
                />

                {isLokleder() && (
                  <NoDivOverflowButton>
                    <Button
                      variant="secondary"
                      onClick={handleModalOpen}
                      leftIcon={<AddOutline24Icon />}
                      size="md"
                    >
                      Opprett ny kvittering
                    </Button>
                  </NoDivOverflowButton>
                )}
              </Flex>
            </Flex>

            <ChecklistTable
              fillouts={displayedFillOuts}
              vehicleSetVariants={vehicleSetVariants}
              employees={employees}
              totalFillOuts={filteredFillOuts.length}
              loading={isChecklistLoading}
              refetch={refetch}
            />

            {fillOuts.length > 0 && (
              <Flex
                justifyContent="end"
                alignItems="center"
                pt="10px"
                pb="24px"
              >
                <Text pr="12px">
                  Viser {displayedFillOuts.length} av {filteredFillOuts.length}{" "}
                  kvitteringer
                </Text>

                {fillOuts.length > 8 && (
                  <Button variant="tertiary" onClick={toggleShowAll}>
                    {showAll ? "Vis færre" : "Vis alle"}
                  </Button>
                )}
              </Flex>
            )}
          </Box>

          {modalOpen && (
            <ChecklistModal
              currentFillOut={null}
              vehicleSetVariants={vehicleSetVariants}
              employees={employees}
              onClose={handleModalClose}
              refetch={refetch}
              potentialSetIdFromSearchString={searchQuery}
            />
          )}
        </VStack>
      </ErrorBoundary>
    </ChecklistHistoryWrapper>
  );
};

export default Checklist;
